// File: plc-frontend/src/HistoryModal.js

import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Line } from 'react-chartjs-2';
import Hammer from 'hammerjs'; // Keep for pinch-to-zoom on touch devices

// This is the component definition that was moved from App.js
const HistoryModal = ({ item, onClose, backendUrl, getAuthHeaders }) => {
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const chartRef = useRef(null);

    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const [startTime, setStartTime] = useState(yesterday.toISOString().slice(0, 16));
    const [endTime, setEndTime] = useState(new Date().toISOString().slice(0, 16));
    const [yMin, setYMin] = useState('');
    const [yMax, setYMax] = useState('');

    useEffect(() => {
        const fetchHistory = async () => {
            const headers = getAuthHeaders();
            if (!headers) {
                setError('Authentication failed. No token found.');
                setLoading(false);
                return;
            }

            try {
                setLoading(true);
                setError('');
                const startUtc = new Date(startTime).toISOString();
                const endUtc = new Date(endTime).toISOString();

                const response = await fetch(
                    `${backendUrl}/api/history?type=${item.type}&address=${item.address}&start=${startUtc}&end=${endUtc}`, {
                        headers: headers
                    }
                );

                if (!response.ok) {
                    throw new Error(`Server Error: ${response.status} ${response.statusText}`);
                }

                const contentType = response.headers.get("content-type");
                if (!contentType || !contentType.includes("application/json")) {
                    throw new TypeError("Backend did not send JSON. Received HTML or other format.");
                }
                
                const data = await response.json();
                setHistory(data || []);
                if (!data || data.length === 0) {
                    setError("No historical data found for this time range.");
                }

            } catch (err) {
                console.error("Failed to fetch history", err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        if (item) {
            fetchHistory();
        }
    }, [item, startTime, endTime, backendUrl, getAuthHeaders]);


    const handleResetView = () => {
        if (chartRef.current) {
            setYMin('');
            setYMax('');
            chartRef.current.resetZoom();
        }
    };

    const chartData = {
        datasets: [{
            label: `${item.name} Value`,
            data: history.map(d => ({ x: new Date(d.time), y: d.value })),
            borderColor: '#61dafb',
            backgroundColor: 'rgba(97, 218, 251, 0.5)',
            borderWidth: 2,
            pointRadius: 1,
            stepped: item.type === 'coil',
        }],
    };

    const chartOptions = useMemo(() => ({
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        scales: {
            x: {
                type: 'time',
                time: { unit: 'minute', tooltipFormat: 'PPpp' },
                title: { display: true, text: 'Time', color: '#FFF' },
                ticks: { color: '#CCC' },
            },
            y: {
                title: { display: true, text: 'Value', color: '#FFF' },
                min: yMin !== '' && !isNaN(yMin) ? Number(yMin) : undefined,
                max: yMax !== '' && !isNaN(yMax) ? Number(yMax) : undefined,
                ticks: { color: '#CCC' },
            }
        },
        plugins: {
            legend: { display: false },
            title: { display: true, text: `History for ${item.name} - Drag to pan, scroll to zoom`, color: '#FFF' },
            zoom: {
                pan: { enabled: true, mode: 'x' },
                zoom: { wheel: { enabled: true }, pinch: { enabled: true }, mode: 'x' },
            },
        },
    }), [yMin, yMax, item.name]);

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content" onClick={e => e.stopPropagation()}>
                <button className="modal-close-button" onClick={onClose}>X</button>
                <h2>History for {item.name}</h2>
                
                <div className="history-controls">
                    <label>Start: <input type="datetime-local" value={startTime} onChange={e => setStartTime(e.target.value)} /></label>
                    <label>End: <input type="datetime-local" value={endTime} onChange={e => setEndTime(e.target.value)} /></label>
                    <span className="y-axis-label">Y-Axis:</span>
                    <label>Min: <input type="number" className="axis-input" value={yMin} onChange={e => setYMin(e.target.value)} placeholder="Auto" /></label>
                    <label>Max: <input type="number" className="axis-input" value={yMax} onChange={e => setYMax(e.target.value)} placeholder="Auto" /></label>
                    <button onClick={handleResetView}>Reset View</button>
                </div>

                {loading ? ( <p>Loading history...</p> ) : 
                 error ? ( <p className="error">{error}</p> ) : 
                 (
                    <>
                        <div className="chart-container-modal">
                            <Line ref={chartRef} options={chartOptions} data={chartData} />
                        </div>
                        <div className="history-table-container">
                            <table className="history-table">
                                <thead>
                                    <tr>
                                        <th>Timestamp</th>
                                        <th>Value</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {history.slice().reverse().map((entry, index) => (
                                        <tr key={index}>
                                            <td>{new Date(entry.time).toLocaleString()}</td>
                                            <td>{entry.value}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default HistoryModal;
